import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { useCriancas } from '../hooks/useCriancas';
import { formatarDataBR, hojeISO } from '../common/data';

/**
 * PDI — Plano de Desenvolvimento Individual, estruturado.
 *
 * Até aqui o PDI era um campo de texto solto dentro do atendimento. Texto
 * corrido cumpre a palavra "PDI" que o card vende, não a prática: sem meta com
 * prazo e sem revisão datada não há como acompanhar nem como mostrar a uma
 * vistoria o que foi combinado e o que aconteceu.
 *
 * A tela é montada na ordem em que a equipe decide: primeiro o que está
 * vencido ou atrasado, depois o plano, depois o histórico — que é consulta.
 */

interface Meta {
  id: number;
  descricao: string;
  criterio: string | null;
  prazo: string;
  id_responsavel: number | null;
  responsavel?: { id: number; nome: string } | null;
  situacao: string;
  vencida?: boolean;
}

interface Revisao {
  id: number;
  data_revisao: string;
  participantes: string | null;
  avaliacao: string;
  proxima_revisao: string | null;
  autor?: { id: number; nome: string } | null;
}

interface Pdi {
  id: number;
  id_aluno: number;
  titulo: string;
  vigencia_inicio: string;
  vigencia_fim: string | null;
  situacao: string;
  texto_base: string | null;
  aluno?: { id: number; nome: string } | null;
  metas: Meta[];
  revisoes: Revisao[];
  resumo: {
    metas: { total: number; alcancadas: number; emAberto: number; vencidas: number; progresso: number | null };
    revisao: { ultima: string | null; proxima: string | null; atrasada: boolean; diasDeAtraso: number | null };
  };
}

const SITUACOES_PDI = [
  { valor: 'rascunho', label: 'Rascunho' },
  { valor: 'vigente', label: 'Vigente' },
  { valor: 'encerrado', label: 'Encerrado' },
];

const SITUACOES_META = [
  { valor: 'nao_iniciada', label: 'Não iniciada' },
  { valor: 'em_andamento', label: 'Em andamento' },
  { valor: 'alcancada', label: 'Alcançada' },
  { valor: 'nao_alcancada', label: 'Não alcançada' },
  { valor: 'suspensa', label: 'Suspensa' },
];

const rotulo = (lista: { valor: string; label: string }[], v: string) =>
  lista.find((i) => i.valor === v)?.label || v;

function mensagemDeErro(e: any, padrao: string) {
  const m = e?.response?.data?.message;
  if (Array.isArray(m)) return m.join(' ');
  return typeof m === 'string' && m.trim() ? m : padrao;
}

export default function PdiSection() {
  const { criancas } = useCriancas();
  const [pdis, setPdis] = useState<Pdi[]>([]);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [aberto, setAberto] = useState<number | null>(null);
  const [criando, setCriando] = useState(false);

  const [novo, setNovo] = useState({ id_aluno: 0, titulo: '', vigencia_inicio: hojeISO(), vigencia_fim: '' });
  const [novaMeta, setNovaMeta] = useState({ descricao: '', criterio: '', prazo: '', id_responsavel: '' });
  const [novaRevisao, setNovaRevisao] = useState({
    data_revisao: hojeISO(), participantes: '', avaliacao: '', proxima_revisao: '',
  });

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await api.get<Pdi[]>('/aee/pdi');
      setPdis(data);
      setErro('');
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não foi possível carregar os PDIs.'));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  async function acao(fn: () => Promise<any>, padrao: string) {
    try {
      await fn();
      setErro('');
      await carregar();
      return true;
    } catch (e) {
      setErro(mensagemDeErro(e, padrao));
      return false;
    }
  }

  const criarPdi = () =>
    acao(async () => {
      await api.post('/aee/pdi', {
        ...novo,
        vigencia_fim: novo.vigencia_fim || null,
      });
      setNovo({ id_aluno: 0, titulo: '', vigencia_inicio: hojeISO(), vigencia_fim: '' });
      setCriando(false);
    }, 'Não foi possível criar o PDI.');

  const adicionarMeta = (idPdi: number) =>
    acao(async () => {
      await api.post(`/aee/pdi/${idPdi}/metas`, {
        descricao: novaMeta.descricao,
        criterio: novaMeta.criterio || null,
        prazo: novaMeta.prazo,
        id_responsavel: novaMeta.id_responsavel || null,
      });
      setNovaMeta({ descricao: '', criterio: '', prazo: '', id_responsavel: '' });
    }, 'Não foi possível adicionar a meta.');

  const mudarSituacaoMeta = (idPdi: number, idMeta: number, situacao: string) =>
    acao(() => api.put(`/aee/pdi/${idPdi}/metas/${idMeta}`, { situacao }), 'Não foi possível atualizar a meta.');

  const removerMeta = (idPdi: number, idMeta: number) =>
    acao(() => api.delete(`/aee/pdi/${idPdi}/metas/${idMeta}`), 'Não foi possível remover a meta.');

  const registrarRevisao = (idPdi: number) =>
    acao(async () => {
      await api.post(`/aee/pdi/${idPdi}/revisoes`, {
        ...novaRevisao,
        participantes: novaRevisao.participantes || null,
        proxima_revisao: novaRevisao.proxima_revisao || null,
      });
      setNovaRevisao({ data_revisao: hojeISO(), participantes: '', avaliacao: '', proxima_revisao: '' });
    }, 'Não foi possível registrar a revisão.');

  return (
    <section className="pagina-secao" style={{ marginTop: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>PDI — Planos de Desenvolvimento Individual</h2>
        <button className="btn-primario" onClick={() => setCriando((v) => !v)}>
          {criando ? 'Cancelar' : '+ Novo PDI'}
        </button>
      </div>
      <p style={{ color: '#666', marginTop: 4 }}>
        Metas com prazo e responsável, e revisões datadas. O progresso é calculado das metas —
        não é digitado.
      </p>

      {erro && <div className="alerta-erro" style={{ margin: '12px 0' }}>{erro}</div>}

      {criando && (
        <div className="cartao" style={{ padding: 16, margin: '12px 0', border: '1px solid #ddd', borderRadius: 8 }}>
          <label>Criança
            <select value={novo.id_aluno} onChange={(e) => setNovo({ ...novo, id_aluno: Number(e.target.value) })}>
              <option value={0}>Selecione...</option>
              {criancas.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </label>
          <label>Título
            <input
              value={novo.titulo}
              placeholder="Ex.: PDI 2026.1"
              onChange={(e) => setNovo({ ...novo, titulo: e.target.value })}
            />
          </label>
          <label>Início da vigência
            <input type="date" value={novo.vigencia_inicio}
              onChange={(e) => setNovo({ ...novo, vigencia_inicio: e.target.value })} />
          </label>
          <label>Fim da vigência (opcional)
            <input type="date" value={novo.vigencia_fim}
              onChange={(e) => setNovo({ ...novo, vigencia_fim: e.target.value })} />
          </label>
          <button className="btn-primario" onClick={criarPdi}>Criar PDI</button>
        </div>
      )}

      {carregando && <p>Carregando…</p>}
      {!carregando && pdis.length === 0 && (
        <p style={{ color: '#666' }}>Nenhum PDI cadastrado ainda.</p>
      )}

      {pdis.map((pdi) => {
        const r = pdi.resumo;
        const expandido = aberto === pdi.id;
        return (
          <div key={pdi.id} style={{ border: '1px solid #ddd', borderRadius: 8, margin: '12px 0', padding: 16 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <strong>{pdi.aluno?.nome || `Criança ${pdi.id_aluno}`}</strong>
              <span style={{ color: '#666' }}>{pdi.titulo}</span>
              <span className="chip">{rotulo(SITUACOES_PDI, pdi.situacao)}</span>
              <span style={{ color: '#666', fontSize: 13 }}>
                vigência {formatarDataBR(pdi.vigencia_inicio)}
                {pdi.vigencia_fim ? ` – ${formatarDataBR(pdi.vigencia_fim)}` : ''}
              </span>
              <button style={{ marginLeft: 'auto' }} onClick={() => setAberto(expandido ? null : pdi.id)}>
                {expandido ? 'Fechar' : 'Abrir'}
              </button>
            </div>

            {/* Progresso: "—" quando não há meta. Zero seria "medi e deu zero". */}
            <div style={{ display: 'flex', gap: 16, marginTop: 10, flexWrap: 'wrap', fontSize: 14 }}>
              <span>
                Progresso:{' '}
                <strong>{r.metas.progresso === null ? '—' : `${r.metas.progresso}%`}</strong>
                {r.metas.progresso === null && (
                  <span style={{ color: '#888' }}> (sem metas que contem)</span>
                )}
              </span>
              <span>{r.metas.alcancadas} de {r.metas.total} alcançadas</span>
              {r.metas.vencidas > 0 && (
                <span style={{ color: '#c62828', fontWeight: 600 }}>
                  {r.metas.vencidas} meta{r.metas.vencidas > 1 ? 's' : ''} vencida{r.metas.vencidas > 1 ? 's' : ''}
                </span>
              )}
              {r.revisao.atrasada && (
                <span style={{ color: '#c62828', fontWeight: 600 }}>
                  revisão atrasada há {r.revisao.diasDeAtraso} dia{r.revisao.diasDeAtraso === 1 ? '' : 's'}
                </span>
              )}
              {!r.revisao.atrasada && r.revisao.proxima && (
                <span style={{ color: '#666' }}>próxima revisão em {formatarDataBR(r.revisao.proxima)}</span>
              )}
              {r.revisao.ultima === null && (
                <span style={{ color: '#888' }}>ainda sem revisão registrada</span>
              )}
            </div>

            {expandido && (
              <div style={{ marginTop: 16 }}>
                {pdi.texto_base && (
                  <p style={{ background: '#fafafa', padding: 10, borderRadius: 6, whiteSpace: 'pre-wrap' }}>
                    {pdi.texto_base}
                  </p>
                )}

                <h4>Metas</h4>
                {pdi.metas.length === 0 && <p style={{ color: '#888' }}>Nenhuma meta neste PDI.</p>}
                {pdi.metas.map((m) => (
                  <div key={m.id} style={{
                    borderLeft: `3px solid ${m.vencida ? '#c62828' : '#ccc'}`,
                    padding: '6px 10px', margin: '8px 0', background: '#fafafa',
                  }}>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{ flex: 1, minWidth: 200 }}>{m.descricao}</span>
                      <span style={{ fontSize: 13, color: m.vencida ? '#c62828' : '#666' }}>
                        prazo {formatarDataBR(m.prazo)}{m.vencida ? ' — vencida' : ''}
                      </span>
                      <span style={{ fontSize: 13, color: '#666' }}>
                        {m.responsavel?.nome || 'sem responsável'}
                      </span>
                      <select value={m.situacao} onChange={(e) => mudarSituacaoMeta(pdi.id, m.id, e.target.value)}>
                        {SITUACOES_META.map((s) => <option key={s.valor} value={s.valor}>{s.label}</option>)}
                      </select>
                      <button onClick={() => removerMeta(pdi.id, m.id)}>remover</button>
                    </div>
                    {m.criterio && (
                      <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>Critério: {m.criterio}</div>
                    )}
                  </div>
                ))}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 10 }}>
                  <label style={{ flex: 2, minWidth: 200 }}>Nova meta
                    <input value={novaMeta.descricao} placeholder="O que se pretende alcançar"
                      onChange={(e) => setNovaMeta({ ...novaMeta, descricao: e.target.value })} />
                  </label>
                  <label style={{ flex: 2, minWidth: 180 }}>Critério (como se mede)
                    <input value={novaMeta.criterio}
                      onChange={(e) => setNovaMeta({ ...novaMeta, criterio: e.target.value })} />
                  </label>
                  <label>Prazo
                    <input type="date" value={novaMeta.prazo}
                      onChange={(e) => setNovaMeta({ ...novaMeta, prazo: e.target.value })} />
                  </label>
                  <button className="btn-primario" onClick={() => adicionarMeta(pdi.id)}>Adicionar meta</button>
                </div>

                <h4 style={{ marginTop: 20 }}>Revisões</h4>
                {pdi.revisoes.length === 0 && <p style={{ color: '#888' }}>Nenhuma revisão registrada.</p>}
                {pdi.revisoes.map((rev) => (
                  <div key={rev.id} style={{ borderLeft: '3px solid #ccc', padding: '6px 10px', margin: '8px 0' }}>
                    <div style={{ fontSize: 13, color: '#666' }}>
                      {formatarDataBR(rev.data_revisao)}
                      {rev.autor?.nome ? ` · ${rev.autor.nome}` : ''}
                      {rev.proxima_revisao ? ` · próxima ${formatarDataBR(rev.proxima_revisao)}` : ''}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{rev.avaliacao}</div>
                    {rev.participantes && (
                      <div style={{ fontSize: 13, color: '#666' }}>Participantes: {rev.participantes}</div>
                    )}
                  </div>
                ))}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 10 }}>
                  <label>Data
                    <input type="date" value={novaRevisao.data_revisao}
                      onChange={(e) => setNovaRevisao({ ...novaRevisao, data_revisao: e.target.value })} />
                  </label>
                  <label style={{ flex: 2, minWidth: 220 }}>Avaliação
                    <input value={novaRevisao.avaliacao} placeholder="O que mudou desde a última revisão"
                      onChange={(e) => setNovaRevisao({ ...novaRevisao, avaliacao: e.target.value })} />
                  </label>
                  <label style={{ flex: 1, minWidth: 160 }}>Participantes
                    <input value={novaRevisao.participantes} placeholder="Quem participou"
                      onChange={(e) => setNovaRevisao({ ...novaRevisao, participantes: e.target.value })} />
                  </label>
                  <label>Próxima revisão
                    <input type="date" value={novaRevisao.proxima_revisao}
                      onChange={(e) => setNovaRevisao({ ...novaRevisao, proxima_revisao: e.target.value })} />
                  </label>
                  <button className="btn-primario" onClick={() => registrarRevisao(pdi.id)}>
                    Registrar revisão
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}
